import { LocalDate } from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import {
  Field,
  Form,
  checkBox,
  constantField,
  selectBox,
  textBox,
} from "trader.ts/ui.forms";
import {
  iban,
  phone_type,
  studentemailid,
  studentid,
  studentphoneid,
  thirdpartysellerid,
} from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import { makeArrayField, oneLineArrayRender } from "../../client/arrayfield";
import { constantFieldWithRender } from "../../client/constantfieldwithrender";
import { dateBoxNullable } from "../../client/dateboxnullable";
import { renderSaveButton } from "../../client/savebutton";
import { country } from "../../domain/config";
import { emailValidation } from "../../lib/emailisvalid";
import {
  mandatoryValidation,
  nameValidation,
  phoneValidationSimple,
  zipValidation,
} from "../../lib/validate";
import {
  allPhoneTypes,
  parsePhoneType,
  phoneType,
} from "../../parsing_from_ur";
import * as routes from "../../routes";
import { translatePhoneType } from "../../translations_specific";
import { selectBoxNullable } from "../../lib/selectboxnullablefield";
import { wrapField } from "../../client/questionsField";

export function render(opts: {
  country: country;
  third_party_sellers: { name: string; id: thirdpartysellerid }[];
  schoolUsesDirectDebits: boolean;
  requireConfirmationForVisualMediaPublicity: boolean;
  student: null | {
    id: studentid;
    firstname: string;
    lastname: string;
    birthday: LocalDate | null;
    street: string;
    number: string;
    bus: string;
    zip: string;
    city: string;
    comments: string;
    vat: string;
    iban: iban | null;
    name_on_iban: string;
    direct_debit_allowed: boolean;
    confirmed_visual_media: boolean | null;
    thirdpartysellerid: thirdpartysellerid | null;
    emails:
      | {
          id: studentemailid;
          email: string;
        }[]
      | null;
    phones:
      | {
          id: studentphoneid;
          number: string;
          description: phone_type | null;
        }[]
      | null;
  };
}): HTMLElement | HTMLElement[] {
  const form = new Form({})
    .addField("id", [], () => constantField(opts.student?.id ?? null))
    .addField("firstname", [], () =>
      textBox({
        initialVal: opts.student?.firstname || "",
        label: "Voornaam",
        validations: [mandatoryValidation, nameValidation],
      }),
    )
    .addField("lastname", [], () =>
      textBox({
        initialVal: opts.student?.lastname || "",
        label: "Achternaam",
        validations: [mandatoryValidation, nameValidation],
      }),
    )
    .addField("street", [], () =>
      textBox({
        initialVal: opts.student?.street || "",
        label: "Straatnaam",
      }),
    )
    .addField("number", [], () =>
      textBox({
        initialVal: opts.student?.number || "",
        label: "Huisnummer",
      }),
    )
    .addField("bus", [], () =>
      textBox({
        initialVal: opts.student?.bus || "",
        label: "Bus",
      }),
    )
    .addField("zip", [], () =>
      textBox({
        initialVal: opts.student?.zip || "",
        label: "Postcode",
        validations: [zipValidation],
      }),
    )
    .addField("city", [], () =>
      textBox({
        initialVal: opts.student?.city || "",
        label: "Woonplaats",
      }),
    )
    .addField("birthday", [], () =>
      dateBoxNullable({
        initialVal: opts.student?.birthday || null,
        label: "Geboortedatum",
      }),
    )
    .addField("vat", [], () =>
      textBox({
        initialVal: opts.student?.vat || "",
        label: "BTW nummer",
      }),
    )
    .addField("emails", [], () =>
      makeArrayField({
        initialVal: opts.student?.emails || [],
        makeSubForm: async (em: { id: studentemailid | null; email: string }) =>
          new Form({})
            .addField("id", [], () => constantFieldWithRender(em.id, []))
            .addField("email", [], () =>
              textBox({
                initialVal: em.email,
                validations: [mandatoryValidation, emailValidation],
              }),
            )
            .build(),
        makeNew: () => ({ id: null, email: "" }),
        render: oneLineArrayRender((t) => t.email === ""),
      }),
    )
    .addField("phones", [], () =>
      makeArrayField({
        initialVal: (opts.student?.phones || []).map((em) => ({
          ...em,
          description: em.description ? parsePhoneType(em.description) : null,
        })),
        makeSubForm: async (em: {
          id: studentphoneid | null;
          number: string;
          description: phoneType | null;
        }) =>
          new Form({})
            .addField("id", [], () => constantFieldWithRender(em.id, []))
            .addField("number", [], () =>
              textBox({
                initialVal: em.number,
                validations: [mandatoryValidation, phoneValidationSimple],
              }),
            )
            .addField(
              "description",
              [],
              async (): Promise<Field<phoneType | null>> =>
                wrapField(
                  await selectBox({
                    values: [
                      ...allPhoneTypes,
                      ...(em.description === null ? ["null" as const] : []),
                    ],
                    initial: em.description || ("null" as const),
                    show: (s) => (s === "null" ? "" : translatePhoneType(s)),
                  }),
                  (t) => (t === "null" ? null : t),
                  (t) => (t === null ? "null" : t),
                ),
            )
            .build(),
        makeNew: () => ({
          id: null,
          number: "",
          description: "CellPhone" as const,
        }),
        render: oneLineArrayRender((t) => t.number === ""),
      }),
    )
    .addField("iban", [], () =>
      textBox({
        initialVal: opts.student?.iban || "",
        label: "IBAN rekening",
      }),
    )
    .addField("name_on_iban", [], () =>
      textBox({
        initialVal: opts.student?.name_on_iban,
        label: "Naam op rekening",
      }),
    )
    .addField("confirmed_visual_media", [], () =>
      opts.requireConfirmationForVisualMediaPublicity
        ? checkBox({
            initialVal: opts.student?.confirmed_visual_media || false,
            label: "Akkoord gegeven voor gebruik van beeldmateriaal voor promotiedoeleinden tijdens online inschrijving",
            class: style.checkbox + style.input_like,
          })
        : constantField(false),
    )
    .addField("direct_debit_allowed", [], () =>
      checkBox({
        initialVal: opts.student?.direct_debit_allowed || false,
        label: "Akkoord voor incasso",
        class: style.checkbox + style.input_like,
      }),
    )
    .addField("thirdpartyseller", [], () => {
      if (opts.third_party_sellers.length === 0) {
        return constantField(null);
      } else {
        const initial =
          opts.third_party_sellers.find(
            (s) => s.id === opts.student?.thirdpartysellerid,
          ) || null;
        return selectBoxNullable({
          values: opts.third_party_sellers,
          initial: initial,
          show: (t) => t.name,
          label: "Vereniging",
        });
      }
    })
    .addField("comments", [], () =>
      textBox({
        type: "textarea",
        initialVal: opts.student?.comments || "",
        label: "Opmerkingen",
      }),
    )
    .build({
      render: (fields) => (
        <div>
          {fields.firstname()}
          {fields.lastname()}
          {fields.street()}
          <div className={style.equal_columns}>
            {fields.number()}
            {fields.bus()}
          </div>
          <div className={style.equal_columns}>
            {fields.zip()}
            {fields.city()}
          </div>

          <div className={style.equal_columns}>
            {fields.birthday()}
            {fields.vat()}
          </div>

          {fields.confirmed_visual_media()}

          {opts.schoolUsesDirectDebits ? (
            <section>
              <div className={style.equal_columns}>
                {fields.iban()}
                {fields.name_on_iban()}
              </div>
              {fields.direct_debit_allowed()}
              <div className={style.formsection_informational}>
                {"Deze informatie wordt gebruikt wanneer je een incasso stuurt naar deze student."}
              </div>
            </section>
          ) : (
            <span></span>
          )}

          {fields.thirdpartyseller()}

          <label for="">{"Email"}</label>
          {fields.emails()}

          <label for="">{"Telefoon"}</label>
          {fields.phones()}

          {fields.comments()}
        </div>
      ),
    });
  return (
    <section>
      <section>{form.render()}</section>
      <div style="text-align: right">
        {renderSaveButton({
          formS: form.s,
          save: (stu) =>
            rpc(routes.saveStudentSpec, undefined, {
              ...stu,
              confirmed_visual_media: opts.student
                ? opts.student.confirmed_visual_media
                : null,
              thirdpartysellerid: stu.thirdpartyseller?.id ?? null,
            }).then((res) => {
              if ("tag" in res && res.tag === "success") {
                return {
                  ...res,
                  redirectUrl: res.link,
                  timeout: 1500,
                };
              }
              return res;
            }),
          timeout: 1500,
          reload: false,
        })}
      </div>
    </section>
  );
}
